<script>
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { IFrameHelper } from 'widget/helpers/utils';
import { frontendURL, conversationUrl } from './helper/URLHelper';

import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  SUMO_CHECK_IS_VIDEO_SERVICE_INTEGRATED,
  SUMO_INIT_EVENT,
  SUMO_OPEN_CHAT_EVENT,
  SUMO_PREFIX,
  SUMO_SEND_BOOKING_LINK_EVENT,
} from '../widget/helpers/constants';
import MessageApi from './api/inbox/message';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      latestChatwootVersion: null,
      reconnectService: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
    isIframe() {
      return IFrameHelper.isIFrame();
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
  },
  mounted() {
    // disable dark mode for iframe
    if (!this.isIframe) {
      this.initializeColorTheme();
      this.listenToThemeChanges();
    }
    this.setLocale(window.chatwootConfig.selectedLocale);
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
    window.addEventListener('message', this.handleOpenConversationMessage);
    window.addEventListener('message', this.handlecheckIsVideoServiceIntegrated);
    window.addEventListener('message', this.handleSendBookingLinkMessage);
  },
  unmounted() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
    window.removeEventListener('message', this.handleOpenConversationMessage);
    window.removeEventListener('message', this.handlecheckIsVideoServiceIntegrated);
    window.removeEventListener('message', this.handleSendBookingLinkMessage);
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );

      if (this.isIframe){
        IFrameHelper.sendMessage({ event: SUMO_INIT_EVENT });
        IFrameHelper.sendMessage({ event: SUMO_CHECK_IS_VIDEO_SERVICE_INTEGRATED });
      }
    },
    handleOpenConversationMessage(event) {
      const data = event?.data;

      if (typeof data === 'string' && data.startsWith(SUMO_PREFIX)) {
        const message = JSON.parse(data.replace(SUMO_PREFIX, ''));

        if (message?.event === SUMO_OPEN_CHAT_EVENT && message?.conversationId) {
          const path = frontendURL(
            conversationUrl({
              accountId: this.currentAccountId,
              id: message.conversationId,
            })
          );
          this.router.push({ path });
        }
      }
    },
    handlecheckIsVideoServiceIntegrated(event) {
      const data = event?.data;
      if (typeof data === 'string' && data.startsWith(SUMO_PREFIX)) {

        const message = JSON.parse(data.replace(SUMO_PREFIX, ''));

        if (message?.event === SUMO_CHECK_IS_VIDEO_SERVICE_INTEGRATED && message?.isVideoServiceIntegrated) {
          this.$store.dispatch('setIsVideoIntegrated', message.isVideoServiceIntegrated);
        }
      }
    },
    async handleSendBookingLinkMessage(event) {
      const data = event?.data;
      if (typeof data !== 'string' || !data.startsWith(SUMO_PREFIX)) return;

      try {
        const message = JSON.parse(data.replace(SUMO_PREFIX, ''));

        if (message?.event === SUMO_SEND_BOOKING_LINK_EVENT && message?.conversationId) {
          // If bookInline is true, the backend handles the booking flow - send command in a private message
          if (message.bookInline) {
            await MessageApi.create({
              conversationId: message.conversationId,
              message: '/createInlineMeeting',
              private: true,
              contentAttributes: {
                items: [{ value: message.meetingTypeId, selected: true }],
              },
            });
            return;
          }

          // For non-inline booking, send the booking link as a message
          if (message?.bookingLink) {
            const bookingMessage = `📅 Book a meeting: ${message.bookingLink}`;

            await MessageApi.create({
              conversationId: message.conversationId,
              message: bookingMessage,
              private: false,
            });

            // Optionally resolve the conversation
            if (message.markAsResolved) {
              await this.$store.dispatch('toggleStatus', {
                conversationId: message.conversationId,
                status: 'resolved',
              });
            }
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error handling send booking link message:', error);
      }
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-white"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <!--    <UpdateBanner :latest-chatwoot-version="latestChatwootVersion" />-->
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}

.multiselect__input {
  margin-bottom: 0px !important;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
